<script setup lang="tsx">
import * as Chartist from 'chartist'
import { ref, onBeforeMount, onMounted, watch, computed } from 'vue'
import http from '@/services/http'
import type { ModuleStatuses, Response } from '@/models/api-generated'
import { drawLines } from '@/components/donut'
import { hasPermission } from '@/services/utils'
import { PermAllMeasureRead, PermOwnMeasureRead } from '@/models/enums-generated'

const currentUser = sessionMap.user
const chart = ref(null)
const result = ref<ModuleStatuses>()
const series: Chartist.FlatSeries = []
const canViewMethods = computed(() => hasPermission(PermOwnMeasureRead, PermAllMeasureRead))

const opts: Chartist.PieChartOptions = {
	donut: true,
	donutWidth: 40,
	startAngle: 45,
	showLabel: true,
	labelInterpolationFnc: (a) => (a ? a + '%' : null),
}

let pieChart: Chartist.PieChart

onBeforeMount(async () => {
	if (!canViewMethods.value) return
	const { data } = await http.get<Response<ModuleStatuses>>('api/dashboard/overallStatus')
	result.value = data.data
})

onMounted(() => {
	pieChart = new Chartist.PieChart(chart.value, { series }, opts)
	drawLines(pieChart, 48)
})

watch(result, (data, _) => {
	if (!data) return

	if (!Object.keys(data).length) {
		data.wna = { percentage: 100, count: 0 }
	}

	series.splice(0, series.length)
	series.push({ className: 'pie1', value: data.i?.percentage || 0 })
	series.push({ className: 'pie-ci', value: data.ci?.percentage || 0 })
	series.push({ className: 'pie2', value: data.pi?.percentage || 0 })
	series.push({ className: 'pie3', value: data.ni?.percentage || 0 })
	series.push({ className: 'pie6', value: data.na?.percentage || 0 })
	series.push({ className: 'pie4', value: data.wna?.percentage || 0 })
	series.push({ className: 'pie-wni', value: data.wni?.percentage || 0 })

	pieChart.update({ series })
})
</script>

<template>
	<div class="d-flex justify-content-center align-items-center" :class="{ active: !result }">
		<div ref="chart" class="chart"></div>
		<div class="position-absolute text-center">
			<h6 class="chart-content m-0">{{ $t('dashboard.overallChart') }}</h6>
			<template v-if="hasPermission(PermOwnMeasureRead) && !hasPermission(PermAllMeasureRead)">
				<small class="text-muted">{{ $t('user.label') }} {{ currentUser.firstname }} {{ currentUser.lastname }}</small>
			</template>
			<template v-else-if="!canViewMethods">
				<small class="text-danger">{{ $t('roles.missingRights') }}</small>
			</template>
		</div>
		<Transition>
			<div v-if="!result && canViewMethods" class="position-absolute">
				<div class="spinner-border text-primary"></div>
			</div>
		</Transition>
	</div>
</template>

<style lang="sass">
.pie-ci
  stroke: url(#greenLinePattern)

.pie-wni
  stroke: url(#greyLinePattern)
</style>

<style lang="sass" scoped>
.v-enter-active,
.v-leave-active,
.chart
  transition: opacity 0.2s ease

.v-enter-from,
.v-leave-to
  opacity: 0

.active
  opacity: .6

.chart
  height: 300px
</style>
